import { store } from '@/store';
import {
    CustomersController,
    ICustomersGetResponse,
    ICustomersInsuranceManualResponse,
    ICustomersInsuranceTestClaim,
    ICustomersInsuranceTestClaimPrescription,
    ICustomersInsuranceTestClaimPrescriptionAdditionalMessage,
    ICustomersTestInsuranceFillResponse,
    ICustomersTestInsuranceResponse,
    ICustomersUserOnboardingResponse
} from '../endpoints/customers';
import { ITestClaimTransferPrescriptions, ITransferPrescriptions } from '../types/interfaces/transfer-prescriptions';
import { IProvider } from '../types/interfaces/provider';
import _ from 'lodash';
import { copayMaxRequestLimit } from '../constants/test-claim';
import dayjs from 'dayjs';

export const getPreviousTestClaim = (insurance: ICustomersInsuranceManualResponse, customer?: ICustomersGetResponse | null) => {
    const previousTestClaims = (customer || store.getState().onboarding.user)?.userOnboarding?.details?.insuranceTestClaims || [];
    return previousTestClaims.find(claim => claim.insuranceId === insurance.id);
};

const getEmptyTestClaim = (insurance: ICustomersInsuranceManualResponse): ICustomersInsuranceTestClaim => ({
    insuranceId: insurance.id,
    prescriptions: []
});

const mapFillForMetadata = (
    fill: ICustomersTestInsuranceFillResponse,
    prescription: ITestClaimTransferPrescriptions
): ICustomersInsuranceTestClaimPrescription => {
    return {
        ndc: fill.ndc,
        medid: +prescription.medid,
        coPay: fill.coPay,
        copayQuantity: prescription.copayQuantity || 30,
        copayDaysSupply: prescription.copayDaysSupply || 30,
        coverageType: fill.insurances[0].coverageType,
        responseStatus: fill.insurances[0].responseStatus,
        rejectCodes: fill.insurances[0].rejectCodes, // reject codes are reason for reject
        additionalMessage: fill.insurances[0].additionalMessage,
        nextAvailableFillDate: fill.insurances[0].nextAvailableFillDate
    };
};

const isNextFillDatePassed = (claim: ICustomersInsuranceTestClaimPrescription) => {
    const nextFillDate = (claim?.additionalMessage as ICustomersInsuranceTestClaimPrescriptionAdditionalMessage)?.nextAvailableFillDate;
    return !!nextFillDate && dayjs(nextFillDate, 'YYYYMMDD').isBefore(dayjs());
};

export const isProductServiceNotCovered = (claim?: ICustomersInsuranceTestClaimPrescription) => {
    const rejectReasonMatches = claim?.rejectCodes?.includes('PRODUCT_SERVICE_NOT_COVERED_PLAN_BENEFIT_EXCLUSION');
    const additionalMessageMatches =
        typeof claim?.additionalMessage?.additionalMessage === 'string' &&
        claim?.additionalMessage?.additionalMessage?.toLowerCase() === 'non par per il medicaid provider number';

    return rejectReasonMatches && additionalMessageMatches;
};

const isReliableCache = (claim?: ICustomersInsuranceTestClaimPrescription) => {
    if (claim?.rejectCodes?.includes('FILL_TOO_SOON')) return !isNextFillDatePassed(claim);
    return true;
};

const getSetKeyForPrescription = (item: ICustomersInsuranceTestClaimPrescription | ITestClaimTransferPrescriptions) => {
    const medId = item.medid;
    let copayQuantity = item.copayQuantity || 30;
    const copayDaysSupply = item.copayDaysSupply || 30;

    if (copayDaysSupply === 90) {
        copayQuantity = Math.ceil(copayQuantity / 3);
    }

    return `${medId}_${copayQuantity}_${30}`;
};

const filterAlreadyTestedPrescriptions = (claim: ICustomersInsuranceTestClaim, allList: ITestClaimTransferPrescriptions[]) => {
    const previousPrescriptions = claim.prescriptions.filter(isReliableCache);
    const previouslyCheckedMedIdList = new Set(
        previousPrescriptions.map(item => {
            return getSetKeyForPrescription(item);
        })
    );
    const remainList = allList.filter(item => !previouslyCheckedMedIdList.has(getSetKeyForPrescription(item)));
    return remainList;
};

const runSingleTestClaim = async (
    insurance: ICustomersInsuranceManualResponse,
    provider: IProvider,
    prescriptionList: ITestClaimTransferPrescriptions[]
) => {
    const deepCopyPrescriptionList = _.cloneDeep(prescriptionList).map(item => {
        let copayDaysSupply = item.copayDaysSupply || 30;
        let copayQuantity = item.copayQuantity || 30;
        if (item.copayDaysSupply === 90) {
            const originalQuantity = item.copayQuantity || 30;
            copayQuantity = Math.ceil(originalQuantity / 3);
            copayDaysSupply = 30;
        }
        item.copayDaysSupply = copayDaysSupply;
        item.copayQuantity = copayQuantity;
        return item;
    });

    const requestModel = {
        id: insurance.id,
        npi: +provider.npi,
        spi: +provider.spi,
        testClaimBillings: deepCopyPrescriptionList.map(item => {
            return {
                ndc: 'placeholder',
                medid: +item.medid,
                quantity: item.copayQuantity,
                daysSupply: item.copayDaysSupply
            };
        })
    };
    const result = await store.dispatch(CustomersController.testInsurance(requestModel));
    // if (result.meta.requestStatus !== 'fulfilled') return null as never;
    if (result.meta.requestStatus !== 'fulfilled') {
        return deepCopyPrescriptionList.map(item => ({
            medid: item.medid,
            error: 'Server error'
        }));
    }

    const payload = result.payload as ICustomersTestInsuranceResponse;

    return payload.patient.fills.map((fill, fillIndex) => {
        return mapFillForMetadata(fill, deepCopyPrescriptionList[fillIndex]);
    });
};

const runTestClaimByProvider = async (
    insurance: ICustomersInsuranceManualResponse,
    prescriptionList: ITestClaimTransferPrescriptions[]
) => {
    const groupedByProvider = _.groupBy(prescriptionList, item => `${item.provider.npi}_${item.provider.spi}`);

    const requests = Object.values(groupedByProvider).map(listByProvider => {
        const { provider } = listByProvider[0];
        return runSingleTestClaim(insurance, provider, listByProvider);
    });

    const results = await Promise.allSettled(requests);
    const [fulfilled] = _.partition(results, item => item.status === 'fulfilled');
    return fulfilled.flatMap(item => item.value);
};

export const updateTestClaimMetadataModel = (
    userOnboarding: ICustomersUserOnboardingResponse,
    claims: ICustomersInsuranceTestClaim[],
    additionalMetaData: Partial<ICustomersUserOnboardingResponse> = {}
): ICustomersUserOnboardingResponse => {
    const oldClaims = (userOnboarding.details!.insuranceTestClaims || []).filter(test => {
        const isOld = claims.every(claim => claim.insuranceId !== test.insuranceId);
        return isOld;
    });

    return {
        ...userOnboarding,
        details: {
            ...userOnboarding.details!,
            insuranceTestClaims: [...oldClaims, ...claims],
            ...additionalMetaData
        }
    };
};

export const isCopayRequestLimitReached = (userOnboarding?: ICustomersUserOnboardingResponse) =>
    (userOnboarding?.details?.copayRequestCount || 0) >= copayMaxRequestLimit;

export const isCopayRequestLimitReachedBasic = (newCount: number) => newCount >= copayMaxRequestLimit;

export const testClaim = async (
    insurance: ICustomersInsuranceManualResponse,
    prescriptions: ITestClaimTransferPrescriptions[],
    customer?: ICustomersGetResponse | null,
    limitReached?: boolean
) => {
    let claim = getPreviousTestClaim(insurance, customer) ?? getEmptyTestClaim(insurance);
    // filter the error ndc cases so that we can get fresh requests for those... thus re running the test
    claim = { ...claim, prescriptions: claim.prescriptions.filter(prescription => !prescription.error) };

    // the error ones are removed ...

    const newPrescriptions = limitReached ? [] : filterAlreadyTestedPrescriptions(claim, prescriptions);
    const newTestResults = await runTestClaimByProvider(insurance, newPrescriptions);

    const prescriptionFills = [
        ...claim.prescriptions.filter(
            prescription =>
                !newTestResults.some(
                    newResult => prescription.copayQuantity === newResult.copayQuantity && prescription.medid === newResult.medid
                )
        ),
        ...newTestResults
    ];

    // Consumer should care about updating metadata on its own
    // If hasUpdate flag is true
    return {
        hasUpdate: !!newTestResults.length,
        updateCount: newTestResults.length,
        result: {
            ...claim,
            prescriptions: prescriptionFills
        } as ICustomersInsuranceTestClaim
    };
};

export const findPrevTestClaimWithOnlyErrors = (insurance: ICustomersInsuranceManualResponse, user: ICustomersGetResponse) => {
    const previousTestClaims = (user || store.getState().onboarding.user)?.userOnboarding?.details?.insuranceTestClaims || [];
    return previousTestClaims.find(
        claim => claim.insuranceId === insurance.id && claim.prescriptions.every(prescription => !!prescription.error)
    );
};